import { useCallback, useMemo, useState } from "react";
import { usePerformGlobalRefresh } from "../../../../components/header/hooks";
import { useCallInReasons } from "../../../../components/team/CallInReasons/callInReasonsHooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useGetLimitedCallOffs } from "../../../../hooks/useGetLimitedCallOffs";
import { useHandleNurseChange } from "../../../../hooks/useHandleNurseChange";
import { trackEvent } from "../../../../utils/analytics";
import { callInCancelMessage, isOtherReasonLabel, resolveCallInReasonLabel } from "../../../../utils/callIns";
import type { CalledOffShift } from "../../../../utils/models";
import { useCancelCallOffs } from "../hooks";

export const useLastCallInsState = ({ limitRows, nurseIds }: { limitRows: number; nurseIds?: number[] }) => {
	const { scheduleDayOf, showErrorSnackbar, isHospital } = useHospitalData();
	const { performGlobalRefresh, isLoading: isGlobalRefreshLoading } = usePerformGlobalRefresh({});
	const [removeCallOffId, setRemoveCallOffId] = useState<string | null>(null);
	const {
		removeNurse,
		isLoading: { removeNurse: removeNurseLoading },
	} = useHandleNurseChange();
	const { mutateAsync: cancelCallOff, isPending: cancelCallOffLoading } = useCancelCallOffs();
	const {
		combinedData,
		isLoading: isLoadingGetLimitedCallOffs,
		updateCallOff,
		refetch: refetchLimitedCallOffs,
		isError: isErrorGetLimitedCallOffs,
	} = useGetLimitedCallOffs({
		nurseIds: nurseIds,
		pageSize: limitRows,
		page: 1,
	});
	// The reason picker is hidden for hospitals (see NurseReasonMenu), so don't fetch their reasons.
	// Same query key as the menu's own `useCallInReasonOptions`, so this is one request, not two.
	const { data: callInReasons } = useCallInReasons({ enabled: !isHospital });
	const reasonLabels = useMemo(() => (callInReasons ?? []).map((reason) => reason.label), [callInReasons]);
	const [clickedNurseInShift, setClickedNurseInShift] = useState<{
		clickedCallOff: CalledOffShift;
		anchorEl: HTMLElement;
	} | null>(null);
	// `undefined` = the user hasn't touched this field yet, so the stored value still drives it.
	const [editedReason, setEditedReason] = useState<string | undefined>(undefined);
	const [editedFreeText, setEditedFreeText] = useState<string | undefined>(undefined);

	const storedMessage = clickedNurseInShift?.clickedCallOff.message ?? "";
	// Derived rather than resolved at click time, so a row clicked before the reasons have loaded still
	// snaps onto its configured label once they arrive, instead of freezing on "no match".
	const resolvedReason = useMemo(() => resolveCallInReasonLabel(storedMessage, reasonLabels), [storedMessage, reasonLabels]);
	// A stored value with no configured counterpart ("Child Care", a since-renamed or archived reason, years
	// of free text) is kept verbatim — never silently relabelled as "Other". The picker offers it as its own
	// option (see NurseReasonMenu), so re-saving a historical row can't rewrite what it says.
	// DEFAULT: a precedence chain, not a guard — each leg is the honest answer when the one before it hasn't
	// been decided. The terminal leg is also live while the reasons query is in flight, which is why the
	// picker is held disabled until it resolves rather than offering a list of one.
	const editMessageType = editedReason ?? resolvedReason ?? storedMessage;
	// When the row shows the catch-all, the stored message is the explanation itself ("Other - car
	// trouble" matches "Other"), so it seeds the free-text box.
	//
	// Keyed off the SHOWN reason, not the resolved one, and only while the user hasn't picked anything.
	// `resolvedReason` alone was wrong for a row storing exactly "Other" that resolves to nothing — a
	// hospital (where the reasons query is disabled, so nothing ever resolves) or a department that no
	// longer configures an "Other" label. There `editMessageType` falls through to "Other", so the box
	// renders and Save demands text, while this expression supplied `""` — leaving Save permanently
	// disabled on a row that used to re-save unchanged. Gating on `editedReason === undefined` is what
	// keeps the other direction right: once she picks the catch-all herself, the PREVIOUS reason's
	// message is not an explanation of it, so the box starts empty.
	// DEFAULT: the `""` is only read where the free-text box is not rendered and not persisted —
	// `handleMenuClose` writes it only when `isOtherReasonLabel(editMessageType)` holds.
	const showsStoredReason = editedReason === undefined;
	const freeTextMessage = editedFreeText ?? (showsStoredReason && isOtherReasonLabel(editMessageType) ? storedMessage : "");

	const handleNurseInShiftClick = useCallback(
		(clickedCallOff: CalledOffShift, anchorEl: HTMLElement) => {
			if (clickedNurseInShift?.clickedCallOff.nurseId === clickedCallOff.nurseId) {
				setClickedNurseInShift(null);
			} else {
				setEditedReason(undefined);
				setEditedFreeText(undefined);
				setClickedNurseInShift({ clickedCallOff, anchorEl });
			}
		},
		[clickedNurseInShift],
	);
	const handleMenuClose = useCallback(
		async (isSubmit: boolean) => {
			if (isSubmit && clickedNurseInShift?.clickedCallOff) {
				// The catch-all writes the typed text; every other reason writes its literal label. Trimmed here
				// rather than per keystroke, so the field can still hold a space mid-sentence. There is no
				// fallback for an empty selection — the menu's save button stays disabled until there is one,
				// rather than guessing a reason the department may not even have configured.
				const message = (isOtherReasonLabel(editMessageType) ? freeTextMessage : editMessageType).trim();
				try {
					trackEvent("Updating deleted shift reason", {
						nurse_id: clickedNurseInShift.clickedCallOff.nurseId,
						shift_id: clickedNurseInShift.clickedCallOff.id,
					});
					await removeNurse({
						nurse: clickedNurseInShift.clickedCallOff,
						...scheduleDayOf(clickedNurseInShift.clickedCallOff),
						message,
					});
					trackEvent("Update deleted shift reason successfully", {
						nurse_id: clickedNurseInShift.clickedCallOff.nurseId,
						date: clickedNurseInShift.clickedCallOff.startTime,
						shift: clickedNurseInShift.clickedCallOff.shiftType,
						reasonCategory: editMessageType,
						reason: message,
					});
					updateCallOff(clickedNurseInShift.clickedCallOff.id, {
						message: message,
					});
				} catch (e) {
					showErrorSnackbar("Failed to update removed shift reason", { nurseId: clickedNurseInShift?.clickedCallOff.id }, e);
					return;
				} finally {
					setClickedNurseInShift(null);
					setEditedReason(undefined);
					setEditedFreeText(undefined);
				}
			} else {
				setClickedNurseInShift(null);
				setEditedReason(undefined);
				setEditedFreeText(undefined);
			}
		},
		[clickedNurseInShift, scheduleDayOf, showErrorSnackbar, removeNurse, editMessageType, freeTextMessage, updateCallOff],
	);
	// Stored raw — trimming per keystroke would eat the space between words as it is typed.
	const handleCustomMessageChange = useCallback((value: string) => {
		setEditedFreeText(value);
	}, []);

	const handleDiscardNurseRemove = useCallback(
		async (nurseCallOff: CalledOffShift) => {
			setRemoveCallOffId(nurseCallOff.id);
			try {
				const updatedCallOffs = await cancelCallOff([nurseCallOff]);
				if (updatedCallOffs.length > 0) {
					const updatedCallOff = updatedCallOffs[0];
					updateCallOff(updatedCallOff.id, {
						message: updatedCallOff.message || callInCancelMessage,
					});
					if (!updatedCallOff.message) {
						//means that we were able to re-add the shift
						await performGlobalRefresh({
							avoidRefreshFor: {
								requestsData: true,
								nursesData: true,
								notificationsData: true,
								departmentData: false,
							},
						});
					}
				} else {
					showErrorSnackbar("Could not cancel call-off: No updated call-off returned", { nurseId: nurseCallOff.nurseId, callOffId: nurseCallOff.id });
				}
			} catch (e) {
				showErrorSnackbar(`Could not cancel call-off ${e}`, { nurseId: nurseCallOff.nurseId, callOffId: nurseCallOff.id }, e);
			}
			setRemoveCallOffId(null);
		},
		[showErrorSnackbar, updateCallOff, cancelCallOff, performGlobalRefresh],
	);

	return {
		isLoadingGetLimitedCallOffs,
		rawData: combinedData.results,
		handleNurseInShiftClick,
		clickedNurseInShift,
		handleMenuClose,
		editMessageType,
		setEditMessageType: setEditedReason,
		freeTextMessage,
		handleCustomMessageChange,
		removeNurseLoading,
		handleDiscardNurseRemove,
		cancelCallOffLoading: isGlobalRefreshLoading || cancelCallOffLoading,
		removeCallOffId,
		refetchLimitedCallOffs,
		isErrorGetLimitedCallOffs,
		updateCallOff,
	};
};
